import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { SECTION_LABEL } from "@/lib/site";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const STEPS = [
    {
        n: "01",
        title: "Discover",
        desc: "We understand the problem, the customer and the outcome you want.",
        id: "discover",
    },
    {
        n: "02",
        title: "Define",
        desc: "We agree on scope, deliverables, timeline and price — in writing.",
        id: "define",
    },
    {
        n: "03",
        title: "Build",
        desc: "We implement and test the solution, keeping you updated as we go.",
        id: "build",
    },
    {
        n: "04",
        title: "Launch",
        desc: "We deploy, hand over cleanly and provide the agreed support.",
        id: "launch",
    },
];

export default function Process() {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 75%", "end 55%"],
    });
    const raw = useTransform(scrollYProgress, [0, 1], [0, 1]);
    const scaleX = useSpring(raw, { stiffness: 80, damping: 24, mass: 0.4 });

    return (
        <section id="process" className="scroll-mt-20 bg-white" data-testid="process-section">
            <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
                <Reveal>
                    <p className={SECTION_LABEL}>Process</p>
                    <h2 className="mt-4 max-w-2xl font-heading text-3xl font-medium tracking-tight text-[#1C1917] md:text-4xl lg:text-[3.25rem] lg:leading-[1.1]">
                        How we <span className="font-serif italic text-[#CF4242]">work.</span>
                    </h2>
                    <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#57504A] md:text-lg">
                        A simple, transparent process — you always know what happens next.
                    </p>
                </Reveal>

                <div ref={ref} className="relative mt-16">
                    <div
                        className="absolute left-7 right-7 top-7 hidden h-[2px] bg-[#F0E4D7] md:block"
                        aria-hidden="true"
                    >
                        <motion.div
                            className="h-full origin-left bg-gradient-to-r from-[#CF4242] to-[#E8764A]"
                            style={reduce ? { transform: "scaleX(1)" } : { scaleX }}
                        />
                    </div>

                    <Stagger className="grid grid-cols-1 gap-10 md:grid-cols-4 md:gap-8">
                        {STEPS.map((s) => (
                            <StaggerItem key={s.id}>
                                <div className="group relative" data-testid={`process-step-${s.id}`}>
                                    <span className="relative flex h-14 w-14 items-center justify-center rounded-full border border-[#EDE3DA] bg-white font-heading text-sm font-semibold text-[#CF4242] shadow-[0_4px_14px_rgb(207,66,66,0.10)] ring-4 ring-[#FBECE9] transition-[transform,background-color,color] duration-500 group-hover:scale-110 group-hover:bg-[#CF4242] group-hover:text-white">
                                        {s.n}
                                    </span>
                                    <h3 className="mt-5 font-heading text-lg font-semibold text-[#1C1917]">
                                        {s.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#57504A]">
                                        {s.desc}
                                    </p>
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </div>
        </section>
    );
}
